/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ClientEvent,
  KnownMembership,
  MatrixError,
  type MatrixClient,
  type Room as MatrixRoom,
} from "matrix-js-sdk";
import { SyncState } from "matrix-js-sdk/lib/sync";
import {
  isLivekitFocus,
  MatrixRTCSessionEvent,
  MembershipManagerEvent,
  Status,
  type LivekitFocus,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import {
  ConnectionState,
  type Room as LivekitRoom,
  Track,
} from "livekit-client";

import { getValue } from "../utils/observable";
import type { MediaDevices } from "../state/MediaDevices";
import type { MuteStates } from "../room/MuteStates";
import {
  technicalRejoinRTCSession,
  type BuildTechnicalRTCSessionJoinOptions,
} from "../rtcSessionHelpers";
import type {
  FocusRecoveryAdapter,
  LocalMediaRecoveryAdapter,
  LocalMediaRestoreResult,
  LocalMediaSnapshot,
  MatrixRecoveryAdapter,
  OwnMembershipRecoveryAdapter,
} from "./CallRecoveryAdapters";
import { createAbortError, raceWithAbort, throwIfAborted } from "./abort";

const MEMBERSHIP_DEACTIVATE_TIMEOUT_MS = 1;
const MEMBERSHIP_STOP_BARRIER_TIMEOUT_MS = 3_000;

class RecoveryWaitTimeoutError extends Error {
  public constructor(events: readonly string[], timeoutMs: number) {
    super(`Ожидание ${events.join("/")} превысило ${timeoutMs}мс`);
    this.name = "RecoveryWaitTimeoutError";
  }
}
/** Matrix adapter одной неизменной комнаты пользовательской call session. */
export class DefaultMatrixRecoveryAdapter implements MatrixRecoveryAdapter {
  public constructor(
    private readonly client: MatrixClient,
    private readonly room: MatrixRoom,
  ) {}

  public async waitForSync(signal: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    if (this.client.getSyncState() === SyncState.Syncing) return;

    await waitForEvent(
      this.client,
      ClientEvent.Sync,
      () => this.client.getSyncState() === SyncState.Syncing,
      signal,
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- Проверка синхронна, интерфейс adapter асинхронный.
  public async assertRoomAccessible(signal: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    const currentRoom = this.client.getRoom(this.room.roomId);
    if (currentRoom !== this.room) {
      throw new MatrixError(
        { errcode: "M_NOT_FOUND", error: "Matrix room недоступна" },
        404,
      );
    }
    if (this.room.getMyMembership() !== KnownMembership.Join) {
      throw new MatrixError(
        { errcode: "M_FORBIDDEN", error: "Пользователь больше не в комнате" },
        403,
      );
    }
  }

  public getSyncState(): SyncState {
    return this.client.getSyncState() ?? SyncState.Stopped;
  }
}

interface TypedEmitter {
  on(event: string, listener: (...args: never[]) => void): unknown;
  off(event: string, listener: (...args: never[]) => void): unknown;
}

/** Ожидает SDK-событие, перепроверяя invariant после каждого вызова. */
async function waitForEvent(
  emitter: TypedEmitter,
  event: string | readonly string[],
  predicate: () => boolean,
  signal: AbortSignal,
  timeoutMs?: number,
): Promise<void> {
  if (predicate()) return;
  const events = typeof event === "string" ? [event] : event;
  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = (): void => {
      for (const eventName of events) emitter.off(eventName, listener);
      signal.removeEventListener("abort", onAbort);
      if (timer !== undefined) clearTimeout(timer);
    };
    const listener = (): void => {
      if (!predicate()) return;
      cleanup();
      resolve();
    };
    const onAbort = (): void => {
      cleanup();
      reject(createAbortError());
    };
    for (const eventName of events) emitter.on(eventName, listener);
    signal.addEventListener("abort", onAbort, { once: true });
    if (timeoutMs !== undefined) {
      timer = setTimeout(() => {
        cleanup();
        reject(new RecoveryWaitTimeoutError(events, timeoutMs));
      }, timeoutMs);
    }
    if (signal.aborted) onAbort();
  });
}

/** Технический restart MatrixRTC без пользовательских side effects. */
export class DefaultOwnMembershipRecoveryAdapter
  implements OwnMembershipRecoveryAdapter
{
  private restartInProgress: Promise<void> | null = null;
  private membershipWasStarted = false;

  public constructor(
    private readonly rtcSession: MatrixRTCSession,
    private readonly options: BuildTechnicalRTCSessionJoinOptions,
  ) {}

  public async restart(signal: AbortSignal): Promise<void> {
    if (this.restartInProgress === null) {
      const restart = this.runRestart(signal).finally(() => {
        if (this.restartInProgress === restart) this.restartInProgress = null;
      });
      this.restartInProgress = restart;
    }
    await raceWithAbort(this.restartInProgress, signal);
  }

  private async runRestart(signal: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    const membershipWasStarted =
      this.membershipWasStarted ||
      this.rtcSession.isJoined() ||
      (this.rtcSession.membershipStatus !== undefined &&
        this.rtcSession.membershipStatus !== Status.Disconnected);

    await this.pauseUpstream();
    throwIfAborted(signal);
    if (
      this.rtcSession.membershipStatus !== undefined &&
      this.rtcSession.membershipStatus !== Status.Disconnected
    ) {
      try {
        await waitForEvent(
          this.rtcSession,
          MembershipManagerEvent.StatusChanged,
          () => this.rtcSession.membershipStatus === Status.Disconnected,
          signal,
          MEMBERSHIP_STOP_BARRIER_TIMEOUT_MS,
        );
      } catch (error) {
        if (!(error instanceof RecoveryWaitTimeoutError)) throw error;
        // Публичный SDK API деактивирует manager синхронно, но не позволяет
        // принудительно дождаться scheduler. Hard recovery не должен навсегда
        // зависнуть в Disconnecting: новый manager повторно восстановит state,
        // даже если старый leave придёт позднее.
      }
    }

    throwIfAborted(signal);
    const options = membershipWasStarted
      ? { ...this.options, notificationType: undefined }
      : this.options;
    await technicalRejoinRTCSession(this.rtcSession, options, false, signal);
    // Ringing относится к пользовательскому входу, а не к technical epoch.
    // Помечаем его использованным только после фактического joinRoomSession:
    // ошибка получения focus до join должна позволить первой успешной attempt
    // отправить notification.
    this.membershipWasStarted = true;
    await waitForEvent(
      this.rtcSession,
      [
        MembershipManagerEvent.StatusChanged,
        MatrixRTCSessionEvent.MembershipsChanged,
      ],
      () => this.isReady(),
      signal,
    );
  }

  public isReady(): boolean {
    const client = this.rtcSession.room.client;
    const userId = client.getUserId();
    const deviceId = client.getDeviceId();
    return (
      this.rtcSession.membershipStatus === Status.Connected &&
      this.rtcSession.memberships.some(
        (membership) =>
          membership.sender === userId &&
          membership.deviceId === deviceId &&
          !membership.isExpired(),
      )
    );
  }

  public async pauseUpstream(): Promise<void> {
    if (!this.rtcSession.isJoined()) return;
    this.membershipWasStarted = true;
    // Нам достаточно синхронной деактивации manager. Его scheduler может
    // завершить leave позже; restart дождётся фактического Disconnected.
    await this.rtcSession.leaveRoomSession(
      MEMBERSHIP_DEACTIVATE_TIMEOUT_MS,
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- Проверка синхронна, интерфейс adapter асинхронный.
  public async resumeUpstream(): Promise<void> {
    if (!this.isReady()) throw new Error("MatrixRTC membership не готова");
  }
}

/** Focus одной attempt фиксируется после свежего Matrix state. */
export class ActiveFocusNotReadyError extends Error {
  public readonly code = "ACTIVE_FOCUS_NOT_READY";

  public constructor() {
    super("Актуальный LiveKit focus ещё не доступен");
    this.name = "ActiveFocusNotReadyError";
  }
}

export class DefaultFocusRecoveryAdapter implements FocusRecoveryAdapter {
  private refreshedFocus: LivekitFocus | null = null;

  public constructor(private readonly rtcSession: MatrixRTCSession) {}

  // eslint-disable-next-line @typescript-eslint/require-await -- Focus читается синхронно из свежего Matrix state.
  public async refresh(signal: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    const focus = this.rtcSession.getActiveFocus();
    if (!focus || !isLivekitFocus(focus)) {
      throw new ActiveFocusNotReadyError();
    }
    this.refreshedFocus = { ...focus };
  }

  public isReady(): boolean {
    const current = this.rtcSession.getActiveFocus();
    return (
      this.refreshedFocus !== null &&
      current !== undefined &&
      isLivekitFocus(current) &&
      current.livekit_service_url === this.refreshedFocus.livekit_service_url &&
      current.livekit_alias === this.refreshedFocus.livekit_alias
    );
  }
}

/** Восстанавливает только пользовательский mic/camera intent текущей session. */
export class DefaultLocalMediaRecoveryAdapter
  implements LocalMediaRecoveryAdapter
{
  private lastRestore: LocalMediaRestoreResult | null = null;
  private readonly safeMuteConfirmed = { audio: false, video: false };

  public constructor(
    private readonly room: LivekitRoom,
    private readonly muteStates: MuteStates | (() => MuteStates),
    private readonly devices: MediaDevices,
  ) {}

  private get currentMuteStates(): MuteStates {
    return typeof this.muteStates === "function"
      ? this.muteStates()
      : this.muteStates;
  }

  public captureSnapshot(): LocalMediaSnapshot {
    return {
      audioEnabled: this.currentMuteStates.audio.enabled,
      videoEnabled: this.currentMuteStates.video.enabled,
      audioDeviceId: getValue(this.devices.audioInput.selected$)?.id,
      videoDeviceId: getValue(this.devices.videoInput.selected$)?.id,
      wasScreenSharing: this.room.localParticipant.isScreenShareEnabled,
    };
  }

  public async stopScreenSharing(): Promise<boolean> {
    const wasEnabled = this.room.localParticipant.isScreenShareEnabled;
    if (wasEnabled) {
      await this.room.localParticipant.setScreenShareEnabled(false);
    }
    return wasEnabled;
  }

  public async restoreRequiredTracks(
    snapshot: LocalMediaSnapshot,
    signal: AbortSignal,
  ): Promise<LocalMediaRestoreResult> {
    throwIfAborted(signal);
    this.safeMuteConfirmed.audio = false;
    this.safeMuteConfirmed.video = false;
    const audio = await this.restoreTrack("audio", snapshot, signal);
    const video = await this.restoreTrack("video", snapshot, signal);
    this.lastRestore = { audio, video };
    return this.lastRestore;
  }

  private async restoreTrack(
    kind: "audio" | "video",
    snapshot: LocalMediaSnapshot,
    signal: AbortSignal,
  ): Promise<"restored" | "safe_muted"> {
    const enabled =
      kind === "audio" ? snapshot.audioEnabled : snapshot.videoEnabled;
    if (!enabled) return "safe_muted";
    const deviceId =
      kind === "audio" ? snapshot.audioDeviceId : snapshot.videoDeviceId;
    try {
      if (kind === "audio") {
        await this.room.localParticipant.setMicrophoneEnabled(true, {
          deviceId,
        });
      } else {
        await this.room.localParticipant.setCameraEnabled(true, { deviceId });
      }
      throwIfAborted(signal);
      return "restored";
    } catch (error) {
      throwIfAborted(signal);
      if (!isUnavailableDeviceError(error)) throw error;
      const muteState =
        kind === "audio"
          ? this.currentMuteStates.audio
          : this.currentMuteStates.video;
      if (!muteState.setEnabled) return "safe_muted";
      if (kind === "audio") {
        await this.room.localParticipant.setMicrophoneEnabled(false);
      } else {
        await this.room.localParticipant.setCameraEnabled(false);
      }
      throwIfAborted(signal);
      muteState.setEnabled(false);
      this.safeMuteConfirmed[kind] = this.isTrackFactuallyMuted(kind);
      return "safe_muted";
    }
  }

  public verifyRequiredTracks(snapshot: LocalMediaSnapshot): boolean {
    if (this.room.state !== ConnectionState.Connected || !this.lastRestore) {
      return false;
    }
    return (
      this.verifyTrack(
        Track.Source.Microphone,
        snapshot.audioEnabled,
        this.lastRestore.audio,
      ) &&
      this.verifyTrack(
        Track.Source.Camera,
        snapshot.videoEnabled,
        this.lastRestore.video,
      ) &&
      !this.room.localParticipant.isScreenShareEnabled
    );
  }

  private verifyTrack(
    source: Track.Source,
    requested: boolean,
    state: "restored" | "safe_muted",
  ): boolean {
    if (!requested) return true;
    if (state === "safe_muted") {
      const kind =
        source === Track.Source.Microphone ? "audio" : ("video" as const);
      return this.safeMuteConfirmed[kind] && this.isTrackFactuallyMuted(kind);
    }
    const publication = this.room.localParticipant.getTrackPublication(source);
    return (
      publication !== undefined &&
      publication.track !== undefined &&
      !publication.isMuted
    );
  }

  private isTrackFactuallyMuted(kind: "audio" | "video"): boolean {
    const participant = this.room.localParticipant;
    const enabled =
      kind === "audio"
        ? participant.isMicrophoneEnabled
        : participant.isCameraEnabled;
    const publication = participant.getTrackPublication(
      kind === "audio" ? Track.Source.Microphone : Track.Source.Camera,
    );
    return (
      !enabled && (publication === undefined || publication.isMuted === true)
    );
  }
}

function isUnavailableDeviceError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    ["NotFoundError", "OverconstrainedError"].includes(error.name)
  );
}

/** Browser online state используется только coordinator, не adapters. */
export const browserCallRecoveryNetwork = {
  isOnline: (): boolean => navigator.onLine,
  async waitUntilOnline(signal: AbortSignal): Promise<void> {
    if (navigator.onLine) return;
    await new Promise<void>((resolve, reject) => {
      const cleanup = (): void => {
        window.removeEventListener("online", onOnline);
        signal.removeEventListener("abort", onAbort);
      };
      const onOnline = (): void => {
        cleanup();
        resolve();
      };
      const onAbort = (): void => {
        cleanup();
        reject(createAbortError());
      };
      window.addEventListener("online", onOnline, { once: true });
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  },
};
